import React from 'react';
import './VerticalsWePower.css';
import Divider from './Divider';
import QuoteMark from './QuoteMark';
import UseCasesOutcomes from './UseCasesOutcomes';
import HowWeBuild from './HowWeBuild';
import { Helmet } from 'react-helmet-async';

const verticals = [
  {
    title: 'Manufacturing Tech',
    description: 'AI-powered process intelligence, cross-process optimization, predictive maintenance, Vision AI, and digital twins - built for cement, heavy industry, and large-scale manufacturing operations.',
    link: '/manufacturing',
    gridColumn: 2,
    gridRow: 1,
    section: 'top'
  },
  {
    title: 'Health Tech',
    description: 'AI-powered fusion diagnostics, patient intelligence via a medical vault, and drug discovery copilots - built for hospitals, clinicians, researchers, healthcare systems, and governments.',
    link: '/healthcare',
    gridColumn: 1,
    gridRow: 1,
    section: 'top'
  },

  {
    title: 'Legal Tech',
    description: 'AI powered Legal Research, Contract automation, litigation intelligence, compliance copilots - all built for law firms, corporates, and courts.',
    link: '/legal',
    gridColumn: 2,
    gridRow: 2,
    section: 'bottom'
  },
];

const VerticalsWePower = () => {
  const topVerticals = verticals.filter(v => v.section === 'top');
  const bottomVerticals = verticals.filter(v => v.section === 'bottom');

  return (
    <>
      <Helmet>
        <title>Verticals We Power - Cognitva Global Technologies Private Limited</title>
        <meta name="description" content="Explore the diverse verticals powered by Cognitva WEAVE™. From Legal Tech to Health Tech, discover how our AI solutions adapt and deliver across industries." />
        <meta name="keywords" content="Cognitva, Verticals We Power, Legal Tech, Health Tech, Financial Services, ESG & Energy, Regulation Tech, AI Solutions" />
        <link rel="canonical" href="https://www.cognitva.ai/verticals-we-power" />
        <meta property="og:title" content="Cognitva Global Technologies Private Limited - Verticals We Power" />
        <meta property="og:description" content="Explore the diverse verticals powered by Cognitva WEAVE™. From Legal Tech to Health Tech, discover how our AI solutions adapt and deliver across industries." />
        <meta property="og:url" content="https://www.cognitva.ai/verticals-we-power" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.cognitva.ai/images/cognitva-logo.png" />
        <meta property="og:image:alt" content="Cognitva Logo" />
        <meta property="og:site_name" content="Cognitva Global Technologies Private Limited" />
      </Helmet>
      <section className="vwp-section">
        <div className="vwp-container">
          <div className="vwp-top-section">
            <div className="vwp-left-container">
              <div className="vwp-left">
                <div className="gb-section-badge"><strong>-</strong> Cross-Industry Impact</div>
                <h2 className="gb-section-title">Where Cognitva WEAVE™ seamlessly adapts and delivers</h2>
                <div className="vwp-intro">
                  <p className="why-cognitva-description">
                    Cognitva is built for regulated, high-stakes environments -
                    where AI must move beyond pilots into <strong>explainable, compliant, production deployments.</strong>
                    <br /><br />
                    <span className="vwp-card-desc">
                      <strong>Healthcare-First</strong> | <strong>Manufacturing-Proven</strong> | <strong>Legal-Piloted</strong></span>
                  </p>
                </div>
              </div>
            </div>
            <div className="vwp-grid-top">
              {topVerticals.map((v, i) => (
                <div
                  className="vwp-card"
                  key={i}
                  style={{ gridColumnStart: v.gridColumn, gridRowStart: v.gridRow }}
                >
                  <div className="vwp-card-accent" style={{ position: 'absolute' }} />
                  <div className="vwp-card-accent" />
                  <div className="vwp-card-title">{v.title}</div>
                  <div className="vwp-card-desc">{v.description}</div>
                  {v.link ? (
                    <a href={v.link} className="vwp-card-link" tabIndex={-1} aria-label="Learn more">
                      <svg width="18" height="18" fill="none" stroke="#23406e" strokeWidth="2" viewBox="0 0 24 24"><path d="M14 3h7v7" /><path d="M10 14L21 3" /><path d="M21 21H3V3" /></svg>
                    </a>
                  ) : (
                    <span className="vwp-card-link disabled" aria-disabled="true">
                      <svg width="18" height="18" fill="none" stroke="#b0b7c3" strokeWidth="2" viewBox="0 0 24 24"><path d="M14 3h7v7" /><path d="M10 14L21 3" /><path d="M21 21H3V3" /></svg>
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="vwp-bottom-section">
            {bottomVerticals.map((v, i) => (
              <div
                className="vwp-card"
                key={i}
                style={{ gridColumnStart: v.gridColumn, gridRowStart: v.gridRow }}
              >
                <div className="vwp-card-accent" />
                <div className="vwp-card-title">{v.title}</div>
                <div className="vwp-card-desc">{v.description}</div>
                {v.link ? (
                  <a href={v.link} className="vwp-card-link" tabIndex={-1} aria-label="Learn more">
                    <svg width="18" height="18" fill="none" stroke="#23406e" strokeWidth="2" viewBox="0 0 24 24"><path d="M14 3h7v7" /><path d="M10 14L21 3" /><path d="M21 21H3V3" /></svg>
                  </a>
                ) : (
                  <span className="vwp-card-link disabled" aria-disabled="true">
                    <svg width="18" height="18" fill="none" stroke="#b0b7c3" strokeWidth="2" viewBox="0 0 24 24"><path d="M14 3h7v7" /><path d="M10 14L21 3" /><path d="M21 21H3V3" /></svg>
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
      <Divider />
      <div className="vwp-quote-section">
        <QuoteMark />
        <span className="vwp-quote-text">Built Once. Deeply Customizable. Delivered Everywhere.</span>
      </div>
      <UseCasesOutcomes />
      <div className="vwp-bottom-quote-section">
        <div className="vwp-bottom-quote-row">
          <span className="vwp-bottom-quote-text">AI that doesn't just act. It reasons, adapts, and collaborates</span>
          <QuoteMark />
        </div>
      </div>
      <Divider />
      <HowWeBuild />
      <Divider />
      <div className="vwp-quote-section">
        <QuoteMark />
        <span className="vwp-quote-text">From healthcare and manufacturing to regulated decision systems, Cognitva powers scalable, defensible AI in production.</span>
      </div>

    </>
  );
};

export default VerticalsWePower;